import { CheckCircleRounded, FileDownloadOutlined, PaymentsOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { fetchFinanceInvoices, type InvoiceListQuery } from "@/api/invoices";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterField } from "@/components/BlotterFilterBar";
import BlotterNote from "@/components/BlotterNote";
import OrgGroupFilter from "@/components/finance/OrgGroupFilter";
import { TruncatedText } from "@/components/ui";
import { InvoiceDetail, StatusChip } from "@/components/usersOrgs/GroupInvoicesPanel";
import { useFinanceInvoiceCreators, useFinanceInvoices } from "@/hooks/useInvoices";
import { usePermissions } from "@/hooks/usePermissions";
import type { Invoice } from "@/interfaces/invoices";
import {
  INVOICE_STATUS,
  INVOICE_STATUS_HELP,
  INVOICE_STATUS_LABEL,
  INVOICE_STATUS_PARAM,
  PAID_LIKE_STATUSES,
} from "@/interfaces/invoices";

const shortDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");
const dateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
const money = (v: string | null, ccy = "usd") =>
  v == null
    ? "—"
    : `${ccy.toUpperCase()} ${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
const credits = (v: string | null) => (v == null ? "—" : `${Number(v).toLocaleString("en-US")} cr`);
const customerName = (i: Invoice) => i.contactName ?? "—";
const customerEmail = (i: Invoice) => i.email ?? "—";
const orgName = (i: Invoice) => i.organizationName ?? "—";
const groupName = (i: Invoice) => i.groupName ?? "—";
const creatorName = (i: Invoice) => i.createdByName ?? "—";

// The blotter shows only the collectible/settled states the finance team acts on.
const STATUS_PILLS: { label: string; value: number | null }[] = [
  { label: "All", value: null },
  { label: "Pending", value: INVOICE_STATUS.pending },
  { label: "Partial", value: INVOICE_STATUS.partial_payment },
  { label: "Overdue", value: INVOICE_STATUS.overdue },
  { label: "Payment failed", value: INVOICE_STATUS.fail_hold },
  { label: "Clearing", value: INVOICE_STATUS.clearing },
  { label: "Paid", value: INVOICE_STATUS.paid },
  { label: "Retrying", value: INVOICE_STATUS.stripe_retry },
];

function StatusPills({
  active,
  onChange,
}: {
  active: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      {STATUS_PILLS.map((p) => {
        const isActive = active === p.value;
        const help = p.value == null ? null : INVOICE_STATUS_HELP[p.value];
        const pill = (
          <Button
            key={p.label}
            size="small"
            onClick={() => onChange(p.value)}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 1.75,
              border: "1px solid",
              borderColor: isActive ? "var(--color-rodeo-black)" : "var(--color-smoke)",
              bgcolor: isActive ? "var(--color-rodeo-black)" : "transparent",
              color: isActive ? "var(--color-white)" : "var(--color-graphite)",
              "&:hover": {
                bgcolor: isActive ? "var(--color-rodeo-black)" : "var(--color-cloud, #f2f0ec)",
              },
            }}
          >
            {p.label}
          </Button>
        );
        return help ? (
          <Tooltip key={p.label} title={help} arrow>
            {pill}
          </Tooltip>
        ) : (
          pill
        );
      })}
    </Box>
  );
}

/**
 * The GFG Finance cross-org invoice blotter — fully server-side (paginated, filtered, sorted in
 * Postgres; the browser only ever holds one page). Status pills + searchable Org/Group selectors feed
 * the query; the Blotter owns search/sort/page. View opens the shared InvoiceDetail.
 */
export default function FinanceInvoicesPanel({
  pinnedOrgId,
}: {
  /**
   * Restrict the list to one organization and hide the Org/Group filter.
   *
   * Set when this panel is mounted inside an organization — Account > Invoices, where "which org"
   * is the context rather than a question. Nothing is enforced here: `/admin/finance/invoices`
   * carries a scopeGuard, so a client only ever sees their own rows whatever is passed. This makes
   * the list AGREE with the page it is on, which matters for GFG staff, whose scope is every org.
   */
  pinnedOrgId?: number;
} = {}) {
  const { isGfg } = usePermissions();
  // Cross-org affordances: the full detail view and the Org/Group + Created-by filters. Gated on
  // TENANCY, not on a capability. This used to read can("send_invoice", "CREDIT"), which worked only
  // because every GFG role happened to carry it — GFG_FINANCE deliberately does not, and would have
  // seen an invoice list it couldn't open.
  const canManage = isGfg;
  const [selected, setSelected] = useState<{ groupId: number; invoiceId: number } | null>(null);
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [orgId, setOrgId] = useState<number | null>(pinnedOrgId ?? null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const { data: creators = [] } = useFinanceInvoiceCreators(canManage);

  if (selected != null) {
    return (
      <InvoiceDetail
        groupId={selected.groupId}
        invoiceId={selected.invoiceId}
        onBack={() => setSelected(null)}
        backLabel="Finance"
      />
    );
  }

  const createdByFilter: FilterField = {
    id: "createdBy",
    label: "Created by",
    options: creators.map((c) => ({ value: String(c.id), label: c.name ?? `User #${c.id}` })),
  };

  // The filters owned by this panel (status/org/group) plus the Blotter's own search/sort/page.
  const baseQuery: InvoiceListQuery = {
    status: statusFilter != null ? INVOICE_STATUS_PARAM[statusFilter] : undefined,
    orgId: orgId ?? undefined,
    groupId: groupId ?? undefined,
  };
  const createdByFrom = (filters: AppliedFilter[]) => {
    const ids = filters.find((f) => f.fieldId === "createdBy")?.values ?? [];
    return ids.length ? ids.join(",") : undefined;
  };

  const useData = (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<Invoice> => {
    const { data, isLoading } = useFinanceInvoices({
      ...baseQuery,
      createdBy: createdByFrom(q.filters),
      search: q.search || undefined,
      sortBy: q.sortBy,
      sortDir: q.sortDir,
      page: q.page,
      limit: q.limit,
    });
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<Invoice> = {
    title: "Invoices",
    searchPlaceholder: "Search number, customer, or email",
    itemNoun: "invoice",
    rowKey: (i) => i.id,
    defaultSortKey: "created",
    defaultSortDir: "desc",
    filterFields: canManage ? [createdByFilter] : [],
    useData,
    exportFilename: "finance-invoices.csv",
    // Server-side CSV export — pulls each page from the API (never a full client-side load).
    exportFetch: async (q) => {
      const res = await fetchFinanceInvoices({
        ...baseQuery,
        createdBy: createdByFrom(q.filters),
        search: q.search || undefined,
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    // Row actions live in the LEADING cell (before the first column). Pay opens the Stripe hosted
    // pay link while the invoice is still collectible; Download pulls the finalized PDF.
    leadingCell: (i) => (
      <Box sx={{ display: "flex", gap: 0.25, whiteSpace: "nowrap" }}>
        {i.hostedInvoiceUrl && !PAID_LIKE_STATUSES.includes(i.status) && (
          <Tooltip title="Pay (Stripe hosted link)">
            <IconButton
              size="small"
              href={i.hostedInvoiceUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Pay"
            >
              <PaymentsOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {i.invoicePdf && (
          <Tooltip title="Download PDF">
            <IconButton
              size="small"
              href={i.invoicePdf}
              target="_blank"
              rel="noreferrer"
              aria-label="Download"
            >
              <FileDownloadOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    columns: [
      {
        key: "number",
        label: "Invoice",
        sortable: true,
        sortValue: (i) => i.invoiceNumber ?? "",
        // GFG opens the full detail; clients (view-only) get plain text — they act via Pay/Download.
        render: (i) =>
          canManage ? (
            <Button
              variant="text"
              size="small"
              sx={{
                textTransform: "none",
                p: 0,
                minWidth: 0,
                fontWeight: 600,
                color: "var(--color-rodeo-black)",
              }}
              onClick={() => setSelected({ groupId: i.groupId, invoiceId: i.id })}
            >
              {i.invoiceNumber ?? `#${i.id}`}
              {i.importedFromStripe && (
                <Tooltip title="Raised by hand in Stripe; imported by Sync with Stripe" arrow>
                  <Box
                    component="span"
                    sx={{ ml: 0.75, fontSize: 11, fontWeight: 600, color: "var(--color-graphite)" }}
                  >
                    From Stripe
                  </Box>
                </Tooltip>
              )}
            </Button>
          ) : (
            <Box sx={{ fontWeight: 500 }}>{i.invoiceNumber ?? `#${i.id}`}</Box>
          ),
      },
      {
        key: "organization",
        label: "Organization",
        sortable: true,
        sortValue: orgName,
        render: (i) => <TruncatedText text={orgName(i)} />,
      },
      {
        key: "group",
        label: "Group",
        sortable: true,
        sortValue: groupName,
        render: (i) => <TruncatedText text={groupName(i)} />,
      },
      {
        key: "sent",
        label: "Sent",
        sortable: true,
        sortValue: (i) => i.issueDate ?? "",
        render: (i) => shortDate(i.issueDate),
      },
      {
        key: "customer",
        label: "Customer",
        sortable: true,
        sortValue: customerName,
        render: (i) => (
          <Box>
            <Typography sx={{ fontSize: 14 }}>{customerName(i)}</Typography>
            <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
              {customerEmail(i)}
            </Typography>
          </Box>
        ),
        exportValue: (i) => `${customerName(i)} <${customerEmail(i)}>`,
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        sortValue: (i) => i.status,
        render: (i) => <StatusChip status={i.status} />,
        exportValue: (i) => INVOICE_STATUS_LABEL[i.status] ?? String(i.status),
      },
      {
        key: "amount",
        label: "Amount",
        sortable: true,
        sortValue: (i) => Number(i.totalAmount ?? 0),
        render: (i) => money(i.totalAmount, i.currency),
        exportValue: (i) => money(i.totalAmount, i.currency),
      },
      {
        key: "credits",
        label: "Credits",
        sortable: true,
        sortValue: (i) => Number(i.creditsToMint ?? 0),
        render: (i) => credits(i.creditsToMint),
        exportValue: (i) => credits(i.creditsToMint),
      },
      {
        key: "provisioned",
        label: "Provisioned?",
        sortable: true,
        sortValue: (i) => (i.isMinted ? 1 : 0),
        render: (i) =>
          i.isMinted ? (
            <Tooltip title="Credits provisioned for this invoice" arrow>
              <CheckCircleRounded sx={{ fontSize: 20, color: "var(--color-good-sage, #3a7d44)" }} />
            </Tooltip>
          ) : (
            <Box component="span" sx={{ color: "var(--color-graphite)", opacity: 0.5 }}>
              —
            </Box>
          ),
        exportValue: (i) => (i.isMinted ? "Yes" : "No"),
      },
      {
        key: "due",
        label: "Due",
        sortable: true,
        sortValue: (i) => i.dueDate ?? "",
        render: (i) => shortDate(i.dueDate),
      },
      {
        key: "created",
        label: "Created",
        sortable: true,
        sortValue: (i) => i.createdAt,
        render: (i) => dateTime(i.createdAt),
      },
      {
        key: "createdBy",
        label: "Created by",
        sortable: true,
        sortValue: creatorName,
        render: creatorName,
      },
    ],
  };

  return (
    <Box>
      {/* GFG-only. The wording is written for the team — cross-client, GFG's own bank, webhooks —
          and this tab is CREDIT/view, so a Client Admin reaches it too and would be reading someone
          else's internal note about their own invoices. */}
      {isGfg && (
        <BlotterNote>
          <strong>Every invoice GFG has raised, across all clients.</strong> An invoice is the
          request for payment; the credits it buys are minted when it is{" "}
          <strong>fully settled</strong>, or earlier if Sales releases them ahead of payment. The
          status says where the money is — <strong>Pending</strong> awaiting the client,{" "}
          <strong>Clearing</strong> paid at Stripe but not yet in GFG's bank, and{" "}
          <strong>Paid</strong> once the deposit lands. Open an invoice to record a payment, issue
          or void it, or confirm a Stripe payout the webhook missed.
        </BlotterNote>
      )}
      <StatusPills active={statusFilter} onChange={setStatusFilter} />
      {/* Org/Group selectors are only meaningful for GFG (cross-org). A client is already scoped to
          their own org, and the orgs endpoint is GFG-only — so hide it for them. */}
      {canManage && pinnedOrgId === undefined && (
        <OrgGroupFilter onOrgChange={setOrgId} onGroupChange={setGroupId} />
      )}
      <Blotter config={config} />
    </Box>
  );
}
